import { Injectable } from '@angular/core';
import { catchError, of, take } from 'rxjs';
import {
  SoapAttribute,
  SoapAttributeSelection,
  SoapCategory,
  SoapPlanItem,
  SoapNotesService,
} from './soap-notes.service';

const SOAP_CATEGORY_BY_SECTION: Record<
  'subjective' | 'objective' | 'assessment' | 'plan',
  SoapCategory
> = {
  subjective: 'S',
  objective: 'O',
  assessment: 'A',
  plan: 'P',
};

@Injectable()
export class SoapNotesScribeSupportService {
  private soapAttributesCatalog: SoapAttribute[] = [];
  private soapPlanItemsCatalog: SoapPlanItem[] = [];
  private isLoadingSoapAttributesCatalog = false;
  private isLoadingSoapPlanItemsCatalog = false;
  private pendingSoapAttributesCatalogCallbacks: Array<(attributes: SoapAttribute[]) => void> = [];
  private pendingSoapPlanItemsCatalogCallbacks: Array<(items: SoapPlanItem[]) => void> = [];

  public ensureSoapAttributesCatalogLoaded(
    soapNotesService: SoapNotesService,
    onLoaded: (attributes: SoapAttribute[]) => void,
  ): void {
    if (this.soapAttributesCatalog.length > 0) {
      onLoaded([...this.soapAttributesCatalog]);
      return;
    }

    if (this.isLoadingSoapAttributesCatalog) {
      this.pendingSoapAttributesCatalogCallbacks.push(onLoaded);
      return;
    }

    this.isLoadingSoapAttributesCatalog = true;
    this.pendingSoapAttributesCatalogCallbacks.push(onLoaded);
    soapNotesService
      .getSoapAttributesCatalog()
      .pipe(
        take(1),
        catchError(() => of([])),
      )
      .subscribe((attributes) => {
        this.isLoadingSoapAttributesCatalog = false;
        this.soapAttributesCatalog = Array.isArray(attributes) ? attributes : [];
        const callbacks = [...this.pendingSoapAttributesCatalogCallbacks];
        this.pendingSoapAttributesCatalogCallbacks = [];
        callbacks.forEach((callback) => callback([...this.soapAttributesCatalog]));
      });
  }

  public getSoapAttributesCatalogSnapshot(): SoapAttribute[] {
    return [...this.soapAttributesCatalog];
  }

  public ensureSoapPlanItemsCatalogLoaded(
    soapNotesService: SoapNotesService,
    onLoaded: (items: SoapPlanItem[]) => void,
  ): void {
    if (this.soapPlanItemsCatalog.length > 0) {
      onLoaded([...this.soapPlanItemsCatalog]);
      return;
    }

    if (this.isLoadingSoapPlanItemsCatalog) {
      this.pendingSoapPlanItemsCatalogCallbacks.push(onLoaded);
      return;
    }

    this.isLoadingSoapPlanItemsCatalog = true;
    this.pendingSoapPlanItemsCatalogCallbacks.push(onLoaded);
    soapNotesService
      .getPlanItemsCatalog()
      .pipe(
        take(1),
        catchError(() => of([])),
      )
      .subscribe((items) => {
        this.isLoadingSoapPlanItemsCatalog = false;
        this.soapPlanItemsCatalog = Array.isArray(items) ? items : [];
        const callbacks = [...this.pendingSoapPlanItemsCatalogCallbacks];
        this.pendingSoapPlanItemsCatalogCallbacks = [];
        callbacks.forEach((callback) => callback([...this.soapPlanItemsCatalog]));
      });
  }

  public getSoapPlanItemsCatalogSnapshot(): SoapPlanItem[] {
    return [...this.soapPlanItemsCatalog];
  }

  public getSoapAttributeOptionsSnapshot(
    section: 'subjective' | 'objective' | 'assessment' | 'plan',
  ): SoapAttributeSelection[] {
    const category = SOAP_CATEGORY_BY_SECTION[section];
    return this.soapAttributesCatalog
      .filter((attribute) => attribute.soapCategory === category)
      .map((attribute) => ({
        id: `${attribute.id}`,
        name: attribute.name,
      }));
  }

  public getSoapPlanItemOptionsSnapshot(): Array<{ id: string; name: string }> {
    return this.soapPlanItemsCatalog.map((item) => ({
      id: `${item.id}`,
      name: item.name,
    }));
  }

  public resolveAttributeLabels(
    section: 'subjective' | 'objective' | 'assessment' | 'plan',
    attributeIds: string[],
  ): string[] {
    const selectedIds = Array.isArray(attributeIds) ? attributeIds.map((id) => `${id}`) : [];
    const labelMap = new Map(
      this.getSoapAttributeOptionsSnapshot(section).map((item) => [item.id, item.name]),
    );

    return selectedIds.map((id) => labelMap.get(id) ?? id);
  }

  public resolvePlanItemsFromSuggestion(
    planItemIds: Array<{ id: string; name?: string }> | string[],
  ): SoapPlanItem[] {
    const selectedIds = Array.isArray(planItemIds)
      ? planItemIds
          .map((item) =>
            typeof item === 'string'
              ? item
              : item?.id === undefined || item?.id === null
                ? ''
                : `${item.id}`,
          )
          .filter(Boolean)
      : [];
    const catalogMap = new Map(this.soapPlanItemsCatalog.map((item) => [`${item.id}`, item]));

    return selectedIds.map((id) => {
      const matched = catalogMap.get(id);
      if (matched) {
        return matched;
      }

      const fallback = Array.isArray(planItemIds)
        ? planItemIds.find(
            (item): item is { id: string; name?: string } =>
              typeof item !== 'string' && `${item?.id ?? ''}` === id,
          )
        : null;

      return {
        Id: id,
        id,
        name: fallback?.name ?? id,
        ItemDescription: fallback?.name ?? id,
      } as SoapPlanItem;
    });
  }
}
